import React, { useEffect, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { Dimmer, Form, Image, Loader } from 'semantic-ui-react';

import { useAppDispatch } from 'src/store';
import { uploadImage } from 'src/thunks/fileUpload';
import { Data } from 'src/interfaces/general';

export type Props = {
  value: string[];
  onChange?: (urls: string[]) => void;
  name?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  onClear?: (url: string) => void;
  disabled?: boolean;
} & DropzoneOptions;

const ImageUploadField: React.FC<Props> = ({
  value,
  onChange,
  name,
  label,
  placeholder,
  error,
  onClear,
  multiple = false,
  disabled,
  ...rest
}) => {
  const [loading, setLoading] = useState<boolean>();
  const dispatch = useAppDispatch();
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    setUrls(value);
  }, [value]);

  const { getRootProps, getInputProps } = useDropzone({
    disabled: disabled || loading,
    multiple,
    async onDrop(acceptedFiles) {
      setLoading(true);

      const { payload } = (await dispatch(
        uploadImage({ images: acceptedFiles }),
      )) as { payload: Data<Model.UPLOAD_URL[]> };

      onChange?.(payload.data.map((d) => d.access_url));

      setLoading(false);
    },
    accept: {
      'image/*': ['.png', '.jpeg', '.jpg'],
    },
    ...rest,
  });

  return (
    <Form.Field error={!!error}>
      <label>{label}</label>
      <div
        aria-disabled={disabled}
        {...getRootProps({ className: `iva_dropzone ${error ? 'error' : ''}` })}
      >
        <input {...getInputProps()} />
        <p className="text-center fs-18 text-gray">{placeholder}</p>
        {loading && (
          <Dimmer active inverted>
            <Loader size="medium">Uploading</Loader>
          </Dimmer>
        )}
      </div>

      {!!urls.length && (
        <div className="mt-24 d-flex flex-wrap gap-16">
          {urls.map((u, i) => (
            <div key={i} className="image-item">
              <Image
                src={u}
                key={i}
                size="tiny"
                className="object-fit-contain"
              />
              <span
                className="clear"
                onClick={() => {
                  onClear?.(u);
                }}
              >
                ×
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <div className="ui pointing above prompt label">{error}</div>}
    </Form.Field>
  );
};

export default ImageUploadField;
